import moment from 'moment';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { getFrontEnv } from '~/javascripts/admin/utils';
import type { PrivateMoneyFormValues } from '..';
import AudioPlayer from '../../../atoms/AudioPlayer';
import CopyToClipboard from '../../../atoms/CopyToClipboard';
import DateTimeRaw from '../../../atoms/DateTime';
import FormErrorMessage from '../../../atoms/FormErrorMessage';
import Grid from '../../../atoms/Grid';
import Img from '../../../atoms/Img';
import Input from '../../../atoms/Input';
import Label from '../../../atoms/Label';
import LocalizedMask from '../../../atoms/LocalizedMask';
import Required from '../../../atoms/Required';
import Select from '../../../atoms/Select';
import TextArea from '../../../atoms/TextArea';
import TextBox from '../../../atoms/TextBox';
import ToggleSwitch from '../../../atoms/ToggleSwitch';
import Tooltip from '../../../atoms/Tooltip';
import BaseMoneySelector from '../BaseMoneySelector';
import OrganizationSelector from '../OrganizationSelector';

type Props = {
  mode: 'create' | 'edit';
  allowedInputs?: 'all' | (keyof PrivateMoneyFormValues)[];
};

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    font-weight: bold;
    color: #676767;
  }

  ${ToggleSwitch} {
    display: block !important;
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }

  ${Select as any} {
    line-height: 1.5;
  }
`;

const DateTime = styled(DateTimeRaw)`
  margin-bottom: 0;
`;

const UnitInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${TextBox} {
    width: 150px;
    margin-right: 8px;
  }
  ${Select as any} {
    width: 150px;
  }
`;

const ImagePreview = styled.div`
  margin-bottom: 8px;

  ${Img} {
    height: 100px;
    object-fit: contain;
  }
`;

const TextValue = styled.u`
  margin-bottom: 0;
  padding: 5px 0;
`;

const CopyURLWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TYPE_OPTIONS = [
  { label: '自家型', value: 'own' },
  { label: '第三者型', value: 'third-party' },
];

const EXPIRATION_TYPE_OPTIONS = [
  { label: '無し', value: 'static' },
  { label: '最終利用時(チャージ/支払い)に更新', value: 'last-update' },
  { label: '最終チャージ時に更新', value: 'last-topup-update' },
];

const POINT_EXPIRATION_TYPE_OPTIONS = [
  { label: '無し', value: 'static' },
  { label: '最終支払い時に更新', value: 'last-payment-update' },
];

const EXPIRES_IN_UNIT_OPTIONS = [
  { label: '月', value: 'months' },
  { label: '日', value: 'days' },
];

const TOTAL_TOPUP_LIMIT_TYPE_OPTIONS = [
  { label: 'なし', value: null },
  { label: '全期間', value: 'entire-period' },
  { label: '期間指定', value: 'range' },
] as const;

const ACCESS_LIMITATION_OPTIONS = [
  { label: '常に表示', value: 'unlimited' },
  { label: '部分一致で表示', value: 'exclusive' },
  { label: '完全一致で表示', value: 'hidden' },
  { label: '非表示（アカウント追加禁止）', value: 'prohibit-add-account' },
  { label: '非表示（カスタムアプリケーションのみ）', value: 'custom-application' },
];

const DISPLAY_MONEY_AND_POINT_OPTIONS = [
  { label: '両方表示', value: 'both' },
  { label: 'マネーのみ表示', value: 'money-only' },
  { label: 'ポイントのみ表示', value: 'point-only' },
];

const BasicInfoForm = ({ mode, allowedInputs }: Props) => {
  const { register, control, setValue, watch } = useFormContext<PrivateMoneyFormValues>();

  const isInputAllowed = (name: keyof PrivateMoneyFormValues) =>
    allowedInputs === 'all' || allowedInputs?.includes(name);

  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const privateMoneyURL = () => {
    const env = getFrontEnv();
    const baseUrl =
      env !== 'production' ? `https://www-${env}.pokepay.jp` : `https://www.pokepay.jp`;

    return `${baseUrl}/private-moneys/${userPrivateMoney.id}`;
  };

  return (
    <FormGrid>
      {mode === 'create' && <OrganizationSelector />}

      {mode === 'edit' && (
        <>
          <Grid.Item size={2}>
            <Label>発行URL</Label>
          </Grid.Item>
          <Grid.Item size={10}>
            <CopyURLWrapper>
              <TextValue>{privateMoneyURL()}</TextValue>
              <CopyToClipboard text={privateMoneyURL()} />
            </CopyURLWrapper>
          </Grid.Item>
        </>
      )}

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>
          マネー名（ふりがな） <Required />
        </Label>
      </Grid.Item>
      <Grid.Item size={4} flexDirection="column">
        <TextBox
          fullWidth
          {...register('furigana', { required: 'マネー名（ふりがな）を入力してください' })}
          disabled={!isInputAllowed('furigana')}
        />
        <FormErrorMessage name="furigana" />
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>
          種類 <Required />
        </Label>
      </Grid.Item>
      <Grid.Item size={4}>
        <Controller
          name="type"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              fullWidth
              isMulti={false}
              options={TYPE_OPTIONS}
              value={TYPE_OPTIONS.find((x) => x.value === value)}
              onChange={(option) => onChange(option?.value)}
              isDisabled={!isInputAllowed('type')}
            />
          )}
        />
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>マネー譲渡</Label>
      </Grid.Item>
      <Grid.Item size={4} alignCenter>
        <Controller
          name="can_use_c2c_transfer"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ToggleSwitch
              checked={value}
              onChange={onChange}
              disabled={!isInputAllowed('can_use_c2c_transfer')}
              checkedText=""
              uncheckedText=""
            />
          )}
        />
        <Label style={{ marginLeft: '1.25rem', color: '#aaaaaa' }}>マネー譲渡を有効にする</Label>
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>併用払い</Label>
      </Grid.Item>
      <Grid.Item size={4} flexDirection="column">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Controller
            name="use_fallback_payment"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                checked={value}
                onChange={(checked) => {
                  onChange(checked);
                  if (!checked) setValue('fallback_private_money_uuid', null);
                }}
                disabled={!isInputAllowed('use_fallback_payment')}
                checkedText=""
                uncheckedText=""
              />
            )}
          />
          <Label style={{ marginLeft: '1.25rem', color: '#aaaaaa' }}>併用払いを有効にする</Label>
        </div>
        {watch('use_fallback_payment') && (
          <BaseMoneySelector mode={mode} allowedInputs={allowedInputs} />
        )}
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>有効期限</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item flexDirection="column">
          <Label>マネー残高の更新</Label>
          <Controller
            name="expiration_type"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                fullWidth
                isMulti={false}
                options={EXPIRATION_TYPE_OPTIONS}
                value={EXPIRATION_TYPE_OPTIONS.find((x) => x.value === value)}
                onChange={(option) => {
                  onChange(option?.value);
                  if (option?.value !== 'static') {
                    setValue('expires_at', null);
                  }
                }}
                isDisabled={!isInputAllowed('expiration_type')}
              />
            )}
          />
        </Grid.Item>
        <Grid.Item flexDirection="column">
          <Label>ポイント残高の更新</Label>
          <Controller
            name="point_expiration_type"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                fullWidth
                isMulti={false}
                options={POINT_EXPIRATION_TYPE_OPTIONS}
                value={POINT_EXPIRATION_TYPE_OPTIONS.find((x) => x.value === value)}
                onChange={(option) => {
                  onChange(option?.value);
                  if (option?.value !== 'static') {
                    setValue('point_expires_at', null);
                  }
                }}
                isDisabled={!isInputAllowed('point_expiration_type')}
              />
            )}
          />
        </Grid.Item>

        <Grid.Item flexDirection="column">
          <Label>マネーの有効期限</Label>
          <UnitInputRow>
            <TextBox
              {...register('expires_in.amount')}
              type="number"
              disabled={!isInputAllowed('expires_in')}
            />
            <Label>単位</Label>
            <Controller
              name="expires_in.unit"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti={false}
                  options={EXPIRES_IN_UNIT_OPTIONS}
                  value={EXPIRES_IN_UNIT_OPTIONS.find((x) => x.value === value)}
                  onChange={(option) => onChange(option?.value ?? 'months')}
                  isDisabled={!isInputAllowed('expires_in')}
                />
              )}
            />
          </UnitInputRow>
          <FormErrorMessage name="expires_in.amount" />
        </Grid.Item>

        <Grid.Item flexDirection="column">
          <Label>ポイントの有効期限</Label>
          <UnitInputRow>
            <TextBox
              {...register('point_expires_in.amount')}
              type="number"
              disabled={!isInputAllowed('point_expires_in')}
            />
            <Label>単位</Label>
            <Controller
              name="point_expires_in.unit"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti={false}
                  options={EXPIRES_IN_UNIT_OPTIONS}
                  value={EXPIRES_IN_UNIT_OPTIONS.find((x) => x.value === value)}
                  onChange={(option) => onChange(option?.value ?? 'months')}
                  isDisabled={!isInputAllowed('point_expires_in')}
                />
              )}
            />
          </UnitInputRow>
          <FormErrorMessage name="point_expires_in.amount" />
        </Grid.Item>

        <Grid.Item flexDirection="column">
          <Label>マネーの有効期限（絶対日付指定）</Label>
          <Controller
            name="expires_at"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DateTime
                isValidDate={(d) => d.isAfter(new Date())}
                locale="ja-JP"
                timeFormat="HH:mm:ss"
                value={value ? moment(value) : undefined}
                onChange={(date) => onChange(date?.toISOString() ?? null)}
                inputProps={{
                  disabled: watch('expiration_type') !== 'static' || !isInputAllowed('expires_at'),
                }}
                showIcon
              />
            )}
          />
          <FormErrorMessage name="expires_at" />
        </Grid.Item>
        <Grid.Item flexDirection="column">
          <Label>ポイントの有効期限（絶対日付指定）</Label>
          <Controller
            name="point_expires_at"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DateTime
                isValidDate={(d) => d.isAfter(new Date())}
                locale="ja-JP"
                timeFormat="HH:mm:ss"
                value={value ? moment(value) : undefined}
                onChange={(date) => onChange(date?.toISOString() ?? null)}
                inputProps={{
                  disabled:
                    watch('point_expiration_type') !== 'static' ||
                    !isInputAllowed('point_expires_at'),
                }}
                showIcon
              />
            )}
          />
          <FormErrorMessage name="point_expires_at" />
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>上限金額</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item flexDirection="column">
          <Label>ウォレット金額</Label>
          <Controller
            name="max_balance"
            control={control}
            rules={{ required: '必須' }}
            render={({ field: { onChange, value } }) => (
              <LocalizedMask value={value.toString()} onChange={onChange}>
                {({ value, ...inputProps }) => (
                  <TextBox
                    syncValue={value}
                    fullWidth
                    min="1"
                    required
                    disabled={!isInputAllowed('max_balance')}
                    {...inputProps}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <FormErrorMessage name="max_balance" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>
            支払い・マネー譲渡・マネー間交換金額
            <Tooltip id="transfer_limit" text="1取引あたりの上限金額です" />
          </Label>
          <Controller
            name="transfer_limit"
            control={control}
            rules={{ required: '必須' }}
            render={({ field: { onChange, value } }) => (
              <LocalizedMask value={value.toString()} onChange={onChange}>
                {({ value, ...inputProps }) => (
                  <TextBox
                    syncValue={value}
                    fullWidth
                    min="1"
                    required
                    disabled={!isInputAllowed('transfer_limit')}
                    {...inputProps}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <FormErrorMessage name="transfer_limit" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>マネーチャージ金額</Label>
          <Controller
            name="money_topup_transfer_limit"
            control={control}
            rules={{ required: '必須' }}
            render={({ field: { onChange, value } }) => (
              <LocalizedMask value={value.toString()} onChange={onChange}>
                {({ value, ...inputProps }) => (
                  <TextBox
                    syncValue={value}
                    fullWidth
                    min="1"
                    required
                    disabled={!isInputAllowed('money_topup_transfer_limit')}
                    {...inputProps}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <FormErrorMessage name="money_topup_transfer_limit" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>
            マネーチャージ累計額の期間設定
            <Tooltip
              id="total_topup_limit.type"
              text="マネーチャージ累計額の集計対象となる期間を選択します"
            />
          </Label>
          <Controller
            name="total_topup_limit.type"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                fullWidth
                isMulti={false}
                options={TOTAL_TOPUP_LIMIT_TYPE_OPTIONS}
                value={TOTAL_TOPUP_LIMIT_TYPE_OPTIONS.find((x) => x.value === value)}
                onChange={(option) => {
                  const type = option?.value ?? null;
                  onChange(type);

                  if (type === 'range') return;

                  setValue('total_topup_limit.started_at', null);

                  if (type === 'entire-period') return;

                  setValue('total_topup_limit.amount', null);
                }}
                isDisabled={!isInputAllowed('total_topup_limit')}
              />
            )}
          />
        </Grid.Item>

        <Grid.Item flexDirection="column">
          <Label>マネーチャージ累計額</Label>
          <Controller
            name="total_topup_limit.amount"
            control={control}
            rules={{ required: watch('total_topup_limit.type') ? '必須' : false, min: 1 }}
            render={({ field: { onChange, value } }) => (
              <LocalizedMask value={value?.toString() ?? ''} onChange={onChange}>
                {({ value, ...inputProps }) => (
                  <TextBox
                    syncValue={value}
                    fullWidth
                    min="1"
                    disabled={
                      !watch('total_topup_limit.type') || !isInputAllowed('total_topup_limit')
                    }
                    {...inputProps}
                  />
                )}
              </LocalizedMask>
            )}
          />
          <FormErrorMessage name="total_topup_limit.amount" />
        </Grid.Item>

        <Grid.Item flexDirection="column" startColumn={2}>
          <Label>マネーチャージ累計額の起算日時</Label>
          <Controller
            name="total_topup_limit.started_at"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DateTime
                locale="ja-JP"
                timeFormat="HH:mm:ss"
                value={value ? moment(value) : undefined}
                onChange={(date) => onChange(date?.toISOString() ?? null)}
                inputProps={{
                  disabled:
                    watch('total_topup_limit.type') !== 'range' ||
                    !isInputAllowed('total_topup_limit'),
                }}
                showIcon
              />
            )}
          />
          <FormErrorMessage name="total_topup_limit.started_at" />
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />

      <Grid.Item size={2}>
        <Label>お客様への表示</Label>
      </Grid.Item>
      <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
        <Grid.Item flexDirection="column">
          <Label>サービス利用規約</Label>
          <TextArea {...register('terms')} disabled={!isInputAllowed('terms')} />
          <FormErrorMessage name="terms" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>プライバシーポリシー</Label>
          <TextArea {...register('privacy_policy')} disabled={!isInputAllowed('privacy_policy')} />
          <FormErrorMessage name="privacy_policy" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>資金決済法上の表記</Label>
          <TextArea {...register('payment_act')} disabled={!isInputAllowed('payment_act')} />
          <FormErrorMessage name="payment_act" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>特定商取引上の表記</Label>
          <TextArea {...register('commercial_act')} disabled={!isInputAllowed('commercial_act')} />
          <FormErrorMessage name="commercial_act" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>ウォレット追加画面</Label>
          <Controller
            name="access_limitation"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                fullWidth
                isMulti={false}
                options={ACCESS_LIMITATION_OPTIONS}
                value={ACCESS_LIMITATION_OPTIONS.find((x) => x.value === value)}
                onChange={(option) => onChange(option?.value)}
                isDisabled={!isInputAllowed('access_limitation')}
              />
            )}
          />
        </Grid.Item>

        {['prohibit-add-account', 'custom-application'].includes(watch('access_limitation')) && (
          <Grid.Item flexDirection="column">
            <Label>カスタムドメイン名</Label>
            <TextBox
              fullWidth
              {...register('custom_domain_name')}
              disabled={!isInputAllowed('custom_domain_name')}
              placeholder={
                !isInputAllowed('custom_domain_name') && !watch('custom_domain_name')
                  ? '設定なし'
                  : ''
              }
            />
            <FormErrorMessage name="custom_domain_name" />
          </Grid.Item>
        )}

        <Grid.Item flexDirection="column" nextRow>
          <Label>マネー/ポイント表示</Label>
          <Controller
            name="display_money_and_point"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                fullWidth
                isMulti={false}
                options={DISPLAY_MONEY_AND_POINT_OPTIONS}
                value={DISPLAY_MONEY_AND_POINT_OPTIONS.find((x) => x.value === value)}
                onChange={(option) => onChange(option?.value)}
                isDisabled={!isInputAllowed('display_money_and_point')}
              />
            )}
          />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>マネー単位の表記（4字以内）</Label>
          <TextBox
            fullWidth
            {...register('unit')}
            maxLength={4}
            disabled={!isInputAllowed('unit')}
          />
          <FormErrorMessage name="unit" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>
            マネー詳細説明（250字以内）
            <Tooltip id="description" text="改行1つで2文字分になります" />
          </Label>
          <TextArea
            {...register('description')}
            maxLength={250}
            disabled={!isInputAllowed('description')}
          />
          <FormErrorMessage name="description" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>ひとことメッセージ（50字以内）</Label>
          <TextArea
            {...register('oneline_message')}
            maxLength={50}
            disabled={!isInputAllowed('oneline_message')}
          />
          <FormErrorMessage name="oneline_message" />
        </Grid.Item>

        <Grid.Item flexDirection="column" nextRow>
          <Label>
            カード画像
            <Tooltip
              id="card-image-info"
              text={
                <span>
                  JPG,PNGのみ設定可能
                  <br />
                  909×576pxを推奨
                </span>
              }
            />
          </Label>
          <Controller
            name="card_image"
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                {value.src && (
                  <ImagePreview>
                    <Img src={value.src} alt="Card Image" />
                  </ImagePreview>
                )}
                <Input
                  fullWidth
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  value={value.file?.name}
                  onChange={(e) =>
                    onChange({
                      file: e.target.files?.[0],
                      src: e.target.files?.[0] ? URL.createObjectURL(e.target.files[0]) : null,
                    })
                  }
                  disabled={!isInputAllowed('card_image')}
                />
              </>
            )}
          />
          <FormErrorMessage name="card_image" />
        </Grid.Item>

        <Grid.Item flexDirection="column">
          <Label>
            ロゴ画像
            <Tooltip
              id="square-small-image-info"
              text={
                <span>
                  JPG,PNGのみ設定可能
                  <br />
                  300×300pxを推奨
                </span>
              }
            />
          </Label>
          <Controller
            name="square_small_image"
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                {value.src && (
                  <ImagePreview>
                    <Img src={value.src} alt="square_small_image" />
                  </ImagePreview>
                )}
                <Input
                  fullWidth
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  value={value.file?.name}
                  onChange={(e) =>
                    onChange({
                      file: e.target.files?.[0] ?? null,
                      src: e.target.files?.[0] ? URL.createObjectURL(e.target.files[0]) : null,
                    })
                  }
                  disabled={!isInputAllowed('square_small_image')}
                />
              </>
            )}
          />
          <FormErrorMessage name="square_small_image" />
        </Grid.Item>

        <Grid.Item flexDirection="column" gap="8px">
          <Label>
            決済音
            <Tooltip
              id="sound"
              text="サイズ1MB以下のwavかmp3の音声ファイルがアップロード可能です"
            />
          </Label>
          <Controller
            name="payment_sound"
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <AudioPlayer url={value.src ?? undefined} />
                <Input
                  type="file"
                  accept="audio/wav, audio/mp3"
                  fullWidth
                  value={value.file?.name}
                  onChange={(e) =>
                    onChange({
                      file: e.target.files?.[0] ?? null,
                      src: e.target.files?.[0] ? URL.createObjectURL(e.target.files[0]) : null,
                    })
                  }
                  disabled={!isInputAllowed('payment_sound')}
                />
              </>
            )}
          />
          <FormErrorMessage name="payment_sound" />
        </Grid.Item>
      </Grid.Item>

      <Grid.Divider />
    </FormGrid>
  );
};

export default BasicInfoForm;
